import { useCallback, useEffect, useRef, useState } from 'react'
import {
  EFFECTIVE_MAX_DURATION_SECONDS,
  EFFECTIVE_MIN_DURATION_SECONDS,
} from './voiceReferenceQualityPolicy'
import { formatDurationClock } from './voiceReferenceQuality'
import {
  clearVoiceReferenceRecordingDraft,
  getVoiceReferenceRecordingDraft,
  upsertVoiceReferenceRecordingDraft,
  type VoiceReferenceRecordingDraftStatus,
} from './voiceReferenceRecordingDraftStore'
import { emptyLiveAudioStats, type LiveAudioStats } from './voiceReferenceReadiness'

export type MicrophonePermissionState =
  | 'idle'
  | 'unsupported'
  | 'denied'
  | 'no_device'
  | 'granted'

export type VoiceRecordingPhase =
  | 'idle'
  | 'requesting'
  | 'recording'
  | 'finalizing'
  | 'recorded'
  | 'error'

export type VoiceRecordingDebugMeta = {
  requestedMime?: string
  actualMime?: string
  blobType?: string
  blobSize?: number
  chunkCount?: number
  stopwatchMs?: number
}

const RECORDER_MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/mp4',
  'audio/aac',
  'audio/wav',
] as const

function pickRecorderMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined
  return RECORDER_MIME_CANDIDATES.find((type) => MediaRecorder.isTypeSupported(type))
}

function mapGetUserMediaError(err: unknown): MicrophonePermissionState {
  const name = (err as DOMException | undefined)?.name || ''
  if (name === 'NotAllowedError' || name === 'PermissionDeniedError') return 'denied'
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError') return 'no_device'
  return 'denied'
}

function draftStatusForPhase(phase: VoiceRecordingPhase): VoiceReferenceRecordingDraftStatus {
  switch (phase) {
    case 'recording':
    case 'requesting':
    case 'finalizing':
      return 'recording'
    case 'recorded':
      return 'recorded_local'
    case 'error':
      return 'error'
    default:
      return 'idle'
  }
}

export function useVoiceMicrophoneRecording(nodeId: string) {
  const initialDraft = getVoiceReferenceRecordingDraft(nodeId)
  const [phase, setPhase] = useState<VoiceRecordingPhase>(() => initialDraft?.phase ?? 'idle')
  const [permissionState, setPermissionState] = useState<MicrophonePermissionState>('idle')
  const [durationMs, setDurationMs] = useState(() => initialDraft?.durationMs ?? 0)
  const [level, setLevel] = useState(0)
  const [liveStats, setLiveStats] = useState<LiveAudioStats>(() => emptyLiveAudioStats())
  const [blob, setBlob] = useState<Blob | null>(() => initialDraft?.blob ?? null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(() => initialDraft?.objectUrl ?? null)
  const [errorKey, setErrorKey] = useState<string | null>(null)
  const [debugMeta, setDebugMeta] = useState<VoiceRecordingDebugMeta | null>(null)

  const streamRef = useRef<MediaStream | null>(null)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<number | null>(null)
  const startedAtRef = useRef<number>(0)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const levelTimerRef = useRef<number | null>(null)
  const liveAccumRef = useRef({
    sumSq: 0,
    samples: 0,
    peak: 0,
    silentFrames: 0,
    speechFrames: 0,
  })
  const previewUrlRef = useRef<string | null>(initialDraft?.objectUrl ?? null)
  const requestedMimeRef = useRef<string | undefined>(initialDraft?.mimeType ?? undefined)
  const nodeIdRef = useRef(nodeId)
  nodeIdRef.current = nodeId

  const persistDraft = useCallback(
    (patch: Parameters<typeof upsertVoiceReferenceRecordingDraft>[1]) => {
      upsertVoiceReferenceRecordingDraft(nodeIdRef.current, patch)
    },
    [],
  )

  const stopTracks = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
  }, [])

  const revokePreviewUrl = useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current)
      previewUrlRef.current = null
    }
    setPreviewUrl(null)
  }, [])

  const stopLiveAnalysis = useCallback(() => {
    if (timerRef.current != null) {
      window.clearInterval(timerRef.current)
      timerRef.current = null
    }
    if (levelTimerRef.current != null) {
      window.clearInterval(levelTimerRef.current)
      levelTimerRef.current = null
    }
    analyserRef.current = null
    if (audioContextRef.current) {
      void audioContextRef.current.close().catch(() => undefined)
      audioContextRef.current = null
    }
    setLevel(0)
    setLiveStats(emptyLiveAudioStats())
    liveAccumRef.current = { sumSq: 0, samples: 0, peak: 0, silentFrames: 0, speechFrames: 0 }
  }, [])

  const releaseCaptureResources = useCallback(() => {
    stopLiveAnalysis()
    recorderRef.current = null
    stopTracks()
  }, [stopLiveAnalysis, stopTracks])

  const resetRecording = useCallback(() => {
    releaseCaptureResources()
    chunksRef.current = []
    revokePreviewUrl()
    setBlob(null)
    setDurationMs(0)
    setLiveStats(emptyLiveAudioStats())
    liveAccumRef.current = { sumSq: 0, samples: 0, peak: 0, silentFrames: 0, speechFrames: 0 }
    setPhase('idle')
    setErrorKey(null)
    setDebugMeta(null)
    requestedMimeRef.current = undefined
    clearVoiceReferenceRecordingDraft(nodeIdRef.current)
  }, [releaseCaptureResources, revokePreviewUrl])

  const cancelRecording = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.ondataavailable = null
      recorderRef.current.onstop = null
      recorderRef.current.stop()
    }
    resetRecording()
  }, [resetRecording])

  useEffect(() => {
    return () => {
      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        recorderRef.current.ondataavailable = null
        recorderRef.current.onstop = null
        recorderRef.current.stop()
      }
      releaseCaptureResources()
      chunksRef.current = []
    }
  }, [releaseCaptureResources])

  useEffect(() => {
    persistDraft({
      phase,
      status: draftStatusForPhase(phase),
      blob,
      objectUrl: previewUrl,
      mimeType: requestedMimeRef.current ?? null,
      durationMs,
    })
  }, [blob, durationMs, persistDraft, phase, previewUrl])

  const startRecording = useCallback(async () => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setPermissionState('unsupported')
      setPhase('error')
      setErrorKey('unsupported')
      return
    }
    if (typeof MediaRecorder === 'undefined') {
      setPermissionState('unsupported')
      setPhase('error')
      setErrorKey('unsupported')
      return
    }

    resetRecording()
    setPhase('requesting')
    persistDraft({ status: 'recording', phase: 'requesting', uploadErrorKey: null })
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream
      setPermissionState('granted')

      const mimeType = pickRecorderMimeType()
      requestedMimeRef.current = mimeType
      if (!mimeType && typeof MediaRecorder !== 'undefined') {
        setPhase('error')
        setErrorKey('unsupportedFormat')
        stopTracks()
        persistDraft({ status: 'error', phase: 'error' })
        return
      }

      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream)
      recorderRef.current = recorder
      chunksRef.current = []

      const audioContext = new AudioContext()
      audioContextRef.current = audioContext
      const source = audioContext.createMediaStreamSource(stream)
      const analyser = audioContext.createAnalyser()
      analyser.fftSize = 2048
      source.connect(analyser)
      analyserRef.current = analyser
      liveAccumRef.current = { sumSq: 0, samples: 0, peak: 0, silentFrames: 0, speechFrames: 0 }
      setLiveStats(emptyLiveAudioStats())

      const timeDomain = new Uint8Array(analyser.fftSize)
      const intervalSec = 0.12
      levelTimerRef.current = window.setInterval(() => {
        analyser.getByteTimeDomainData(timeDomain)
        let frameSumSq = 0
        let framePeak = 0
        for (let i = 0; i < timeDomain.length; i += 1) {
          const v = ((timeDomain[i] ?? 128) - 128) / 128
          const abs = Math.abs(v)
          framePeak = Math.max(framePeak, abs)
          frameSumSq += v * v
        }
        const frameRms = Math.sqrt(frameSumSq / Math.max(1, timeDomain.length))
        const accum = liveAccumRef.current
        accum.sumSq += frameSumSq
        accum.samples += timeDomain.length
        accum.peak = Math.max(accum.peak, framePeak)
        if (framePeak < 0.01) accum.silentFrames += 1
        else accum.speechFrames += 1
        const frameCount = Math.max(1, accum.silentFrames + accum.speechFrames)
        const meanRms = accum.samples > 0 ? Math.sqrt(accum.sumSq / accum.samples) : 0
        const speechSeconds = accum.speechFrames * intervalSec
        const silenceRatio = accum.silentFrames / frameCount
        setLevel(Math.min(1, frameRms * 4))
        setLiveStats({
          meanRms,
          peak: accum.peak,
          silenceRatio,
          speechSeconds,
          frameCount,
        })
      }, Math.round(intervalSec * 1000))

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data)
      }

      recorder.onstop = () => {
        const chunks = chunksRef.current.slice()
        const actualMime = recorder.mimeType || requestedMimeRef.current || 'audio/webm'
        const recordedBlob = new Blob(chunks, { type: actualMime })
        const stopwatchMs = Math.max(0, Date.now() - startedAtRef.current)
        const meta: VoiceRecordingDebugMeta = {
          requestedMime: requestedMimeRef.current,
          actualMime,
          blobType: recordedBlob.type,
          blobSize: recordedBlob.size,
          chunkCount: chunks.length,
          stopwatchMs,
        }
        setDebugMeta(meta)

        releaseCaptureResources()
        chunksRef.current = []

        if (recordedBlob.size <= 0) {
          setBlob(null)
          setPreviewUrl(null)
          setPhase('error')
          setErrorKey('captureFailed')
          persistDraft({
            status: 'error',
            phase: 'error',
            blob: null,
            objectUrl: null,
            durationMs: stopwatchMs,
            quality: null,
            uploadErrorKey: null,
          })
          return
        }

        revokePreviewUrl()
        const url = URL.createObjectURL(recordedBlob)
        previewUrlRef.current = url
        setBlob(recordedBlob)
        setPreviewUrl(url)
        setDurationMs(stopwatchMs)
        setPhase('recorded')
        persistDraft({
          status: 'recorded_local',
          phase: 'recorded',
          blob: recordedBlob,
          objectUrl: url,
          mimeType: actualMime,
          durationMs: stopwatchMs,
          createdAt: Date.now(),
          quality: null,
          uploadErrorKey: null,
        })
      }

      startedAtRef.current = Date.now()
      setDurationMs(0)
      recorder.start(250)
      setPhase('recording')
      persistDraft({ status: 'recording', phase: 'recording' })

      timerRef.current = window.setInterval(() => {
        const elapsed = Date.now() - startedAtRef.current
        setDurationMs(elapsed)
        if (elapsed >= EFFECTIVE_MAX_DURATION_SECONDS * 1000) {
          if (recorderRef.current && recorderRef.current.state === 'recording') {
            setPhase('finalizing')
            try {
              recorderRef.current.requestData()
            } catch {
              /* optional */
            }
            recorderRef.current.stop()
          }
        }
      }, 200)
    } catch (err) {
      releaseCaptureResources()
      chunksRef.current = []
      const mapped = mapGetUserMediaError(err)
      setPermissionState(mapped)
      setPhase('error')
      setErrorKey(mapped === 'no_device' ? 'noDevice' : 'denied')
      persistDraft({ status: 'error', phase: 'error' })
    }
  }, [persistDraft, releaseCaptureResources, resetRecording, revokePreviewUrl, stopTracks])

  const stopRecording = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state === 'recording') {
      setPhase('finalizing')
      persistDraft({ status: 'recording', phase: 'finalizing' })
      setDurationMs(Math.max(0, Date.now() - startedAtRef.current))
      try {
        recorderRef.current.requestData()
      } catch {
        /* optional */
      }
      recorderRef.current.stop()
      return
    }
    if (timerRef.current != null) {
      window.clearInterval(timerRef.current)
      timerRef.current = null
    }
  }, [persistDraft])

  const minimumReached = durationMs >= EFFECTIVE_MIN_DURATION_SECONDS * 1000
  const recommendedReached = durationMs >= 30_000

  return {
    phase,
    permissionState,
    durationMs,
    durationLabel: formatDurationClock(durationMs),
    level,
    liveStats,
    blob,
    previewUrl,
    errorKey,
    debugMeta,
    minimumReached,
    recommendedReached,
    startRecording,
    stopRecording,
    cancelRecording,
    resetRecording,
    persistDraft,
  }
}
